import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import { AppHolder, ThemedGlobalStyle } from './settings';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Login } from './modules/AuthPages';
import { Dashboard } from './modules/Dashboard';
import RootLayout from './components/layout/RootLayout';
import { darkTheme } from './settings/appStyle/theme';
import { UploadDocuments } from './modules/UploadDocuments';
import { Application } from './modules/ApplicationDetails';
import Drive from './modules/Drive/Drive';

const queryClient = new QueryClient();

// ============================================
// Protected Route
// Renders Login directly in place if not authenticated
// ============================================
const ProtectedRoute = () => {
  const { session } = useAuth();
  const isLoggedIn = Boolean(session?.token);

  if (!isLoggedIn) {
    return <Login />;
  }

  return <Outlet />;
};

// ============================================
// Protected User Routes
// ============================================
const UserRoutes = () => {
  return (
    <Routes>
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<RootLayout />}>
          {/* "/" */}
          <Route index element={<Dashboard />} />
          <Route path="application/:id" element={<Drive />} />
          <Route path="uploadDocuments" element={<UploadDocuments />} />
          {/* <Route path="drive" element={<Drive />} /> */}
        </Route>
      </Route>
    </Routes>
  );
};

// ============================================
// Dynamic root handler
// Logged in -> Dashboard flow
// Logged out -> render Login directly
// ============================================
const UserOrPublicRoot = () => {
  const { session } = useAuth();
  const isLoggedIn = Boolean(session?.token);

  if (!isLoggedIn) {
    return (
      <AppHolder id="app-global">
        <Login />
      </AppHolder>
    );
  }

  return (
    <AppHolder id="app-global">
      <UserRoutes />
    </AppHolder>
  );
};

// ============================================
// App Router
// ============================================
const AppRouter = () => {
  return (
    <Routes>
      {/* "/" and all other pages: shows Login inline if logged out, Dashboard flow if logged in */}
      <Route path="/*" element={<UserOrPublicRoot />} />

      {/* fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// ============================================
// Theme wrapper
// ============================================
const SecuredApp = () => {
  return (
    <ThemeProvider theme={darkTheme}>
      <ThemedGlobalStyle />
      <QueryClientProvider client={queryClient}>
        <AppRouter />
      </QueryClientProvider>
    </ThemeProvider>
  );
};

// ============================================
// App
// ============================================
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SecuredApp />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
